/**
 * Ramp curves and their phase integrals — the mathematical heart of the
 * cross-engine contract.
 *
 * Oscillator phase is NEVER accumulated sample-by-sample. It is computed in
 * closed form as the integral of frequency over time from the segment start,
 * plus the precomputed cumulative phase of all earlier segments. This makes
 * phase (a) continuous across segment boundaries by construction — frequency
 * jumps at boundaries cannot click, and (b) independent of render block size,
 * so streaming playback and one-shot offline renders are sample-identical.
 *
 * Time convention (contract): the time of sample n is `n / sampleRate` as a
 * double. `hold` uses v0 throughout; v1 is ignored (by convention presets
 * store v0 === v1 for hold segments).
 */

import type { Curve } from "../../schema/types.ts";

export interface CompiledRamp {
  /** Value at absolute time t (t0 <= t < t1). */
  valueAt(t: number): number;
  /** Integral of the value from t0 to t, in value·seconds (cycles for Hz). */
  phaseAt(t: number): number;
  /** Integral over the whole segment (used to accumulate boundary phase). */
  readonly totalPhase: number;
}

export function compileRamp(
  curve: Curve,
  v0: number,
  v1: number,
  t0: number,
  t1: number,
): CompiledRamp {
  const T = t1 - t0;
  if (curve === "hold" || v0 === v1) {
    return {
      valueAt: () => v0,
      phaseAt: (t) => v0 * (t - t0),
      totalPhase: v0 * T,
    };
  }
  if (curve === "linear") {
    const k = (v1 - v0) / T;
    return {
      valueAt: (t) => v0 + k * (t - t0),
      phaseAt: (t) => {
        const tau = t - t0;
        return v0 * tau + 0.5 * k * tau * tau;
      },
      totalPhase: 0.5 * (v0 + v1) * T,
    };
  }
  // exp: geometric glide v(t) = v0 * r^(tau/T). Validator guarantees v0,v1 > 0.
  const lnr = Math.log(v1 / v0);
  const a = lnr / T;
  return {
    valueAt: (t) => v0 * Math.exp(a * (t - t0)),
    phaseAt: (t) => (v0 / a) * (Math.exp(a * (t - t0)) - 1),
    totalPhase: (T * (v1 - v0)) / lnr,
  };
}

/** Fractional part — phase in cycles is reduced with this before waveshaping. */
export function frac(x: number): number {
  return x - Math.floor(x);
}
